import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["dropdown", "selected", "icon", "optionsWrapper", "options", "searchInput", "noResults", "chipsContainer"]
    static values = { dropdownOpen: Boolean }

    connect() {
        this.dropdownOpenValue = false
        this.focusedOptionIndex = -1
        this.setupEventListeners()
        this.updateSelectedText()
        this.updateChips()
        this.updateCheckboxStates()
    }

    setupEventListeners() {
        this.boundHandleClickOutside = this.handleClickOutside.bind(this)
        this.boundHandleResize = this.handleResize.bind(this)
        this.boundHandleFocusOut = this.handleFocusOut.bind(this)
        document.addEventListener("click", this.boundHandleClickOutside)
        window.addEventListener("resize", this.boundHandleResize)
        this.element.addEventListener("focusout", this.boundHandleFocusOut)

        if (!this.hasOptionsTarget) return

        this.optionsTarget.querySelectorAll('input[type="radio"]').forEach(radio => {
            radio.addEventListener("change", this.handleOptionChange.bind(this))
        })
        
        this.optionsTarget.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
            checkbox.addEventListener("change", this.handleCheckboxChange.bind(this))
            checkbox.addEventListener("click", (event) => {
                event.stopPropagation()
                setTimeout(() => {
                    if (this.hasSearchInputTarget) {
                        this.searchInputTarget.focus()
                    }
                }, 10)
            })
        })
        
        this.optionsTarget.querySelectorAll('label').forEach(label => {
            label.addEventListener("click", (event) => {
                const radio = label.querySelector('input[type="radio"]')
                const checkbox = label.querySelector('input[type="checkbox"]')
                
                if (radio && !radio.disabled) {
                    requestAnimationFrame(() => {
                        this.closeDropdown()
                    })
                }
                if (checkbox) {
                    event.stopPropagation()
                    setTimeout(() => {
                        if (this.hasSearchInputTarget && this.dropdownOpenValue) {
                            this.searchInputTarget.focus()
                        }
                    }, 10)
                }
            })
        })
    }

    disconnect() {
        document.removeEventListener("click", this.boundHandleClickOutside)
        window.removeEventListener("resize", this.boundHandleResize)
        this.element.removeEventListener("focusout", this.boundHandleFocusOut)
    }

    handleResize() {
        if (this.dropdownOpenValue && this.hasOptionsWrapperTarget) {
            requestAnimationFrame(() => this.calculateMaxHeight())
        }
    }

    handleFocusOut(event) {
        setTimeout(() => {
            const activeElement = document.activeElement
            if (activeElement && activeElement.type === 'checkbox' && this.element.contains(activeElement)) {
                return
            }
            
            if (!this.element.contains(activeElement)) {
                this.closeDropdown()
            }
        }, 150)
    }

    toggle() {
        this.dropdownOpenValue ? this.closeDropdown() : this.openDropdown()
    }

    handleKeydown(event) {
        if ((event.key === 'Enter' || event.key === ' ') && !this.dropdownOpenValue) {
            event.preventDefault()
            this.toggle()
            return
        }
        
        if (this.dropdownOpenValue) {
            switch(event.key) {
                case 'ArrowDown':
                    event.preventDefault()
                    this.focusNextOption()
                    break
                case 'ArrowUp':
                    event.preventDefault()
                    this.focusPreviousOption()
                    break
                case 'Enter':
                    event.preventDefault()
                    this.selectFocusedOption()
                    break
                case 'Escape':
                    event.preventDefault()
                    this.closeDropdown()
                    if (this.hasDropdownTarget) {
                        this.dropdownTarget.focus()
                    }
                    break
            }
        }
    }

    handleSearchInputKeydown(event) {
        if (!this.dropdownOpenValue) return

        switch(event.key) {
            case 'ArrowDown':
                event.preventDefault()
                this.focusNextOption()
                break
            case 'ArrowUp':
                event.preventDefault()
                this.focusPreviousOption()
                break
            case 'Enter':
                event.preventDefault()
                this.selectFocusedOption()
                break
            case 'Escape':
                event.preventDefault()
                this.closeDropdown()
                if (this.hasDropdownTarget) {
                    this.dropdownTarget.focus()
                }
                break
        }
    }

    getVisibleOptions() {
        if (!this.hasOptionsTarget) return []
        
        const allInputs = this.optionsTarget.querySelectorAll('input[type="radio"], input[type="checkbox"]')
        return Array.from(allInputs).filter(input => {
            const label = input.closest('label')
            if (!label) return false
            
            const parentDiv = label.closest('div')
            if (parentDiv && parentDiv.style.display === 'none') return false
            
            const groupContainer = label.closest('.pillar-group-container')
            if (groupContainer && groupContainer.style.display === 'none') return false
            
            return true
        })
    }

    focusNextOption() {
        const visibleOptions = this.getVisibleOptions()
        if (visibleOptions.length === 0) return
        
        this.focusedOptionIndex = (this.focusedOptionIndex + 1) % visibleOptions.length
        this.highlightOption(visibleOptions[this.focusedOptionIndex])
    }

    focusPreviousOption() {
        const visibleOptions = this.getVisibleOptions()
        if (visibleOptions.length === 0) return
        
        if (this.focusedOptionIndex <= 0) {
            this.focusedOptionIndex = visibleOptions.length - 1
        } else {
            this.focusedOptionIndex--
        }
        this.highlightOption(visibleOptions[this.focusedOptionIndex])
    }

    highlightOption(option) {
        if (!option) return
        
        if (this.hasOptionsTarget) {
            this.optionsTarget.querySelectorAll('label').forEach(label => {
                label.style.backgroundColor = ''
                label.style.outline = ''
            })
        }
        
        const label = option.closest('label')
        if (label) {
            label.style.backgroundColor = '#E3F2FD'
            label.style.outline = '2px solid #1565C0'
            
            label.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
    }

    selectFocusedOption() {
        const visibleOptions = this.getVisibleOptions()
        if (this.focusedOptionIndex < 0 || this.focusedOptionIndex >= visibleOptions.length) return
        
        const option = visibleOptions[this.focusedOptionIndex]
        if (!option || option.disabled) return
        
        if (option.type === 'checkbox') {
            // For checkboxes, click the label (this matches the test expectations)
            const label = option.closest('label')
            if (label) {
                label.click()
            }
        } else if (option.type === 'radio') {
            // For radio buttons, set checked and dispatch change event
            option.checked = true
            option.dispatchEvent(new Event('change', { bubbles: true }))
        } else {
            // Do nothing for other input types
            return
        }
    }

    openDropdown(event) {
        if (event) event.stopPropagation()
        if (this.dropdownOpenValue) return

        this.dropdownOpenValue = true
        this.focusedOptionIndex = -1
        this.updateDropdownVisibility()
        
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.removeAttribute('readonly')
            this.showAllOptions()
            setTimeout(() => this.searchInputTarget?.focus(), 0)
        }
    }

    closeDropdown() {
        this.dropdownOpenValue = false
        this.focusedOptionIndex = -1
        this.updateDropdownVisibility()
        
        if (this.hasOptionsTarget) {
            this.optionsTarget.querySelectorAll('label').forEach(label => {
                label.style.backgroundColor = ''
                label.style.outline = ''
            })
        }
        
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.setAttribute('readonly', 'readonly')
            this.searchInputTarget.value = ""
            this.updateSelectedText()
        }
    }

    handleClickOutside(event) {
        if (!this.dropdownOpenValue) return
        
        if (this.element.contains(event.target)) return
        
        this.closeDropdown()
    }

    handleOptionChange(event) {
        const optionText = this.getLabelText(event.target)
        this.updateSelectedText(optionText || event.target.value)
        this.closeDropdown()
    }

    handleCheckboxChange() {
        this.updateSelectedText()
        this.updateChips()
        this.updateCheckboxStates()
    }

    getLabelText(input) {
        const label = input.closest('label')
        if (label) {
            const clone = label.cloneNode(true)
            clone.querySelector('input')?.remove()
            const text = clone.textContent.trim()
            if (text) return text
        }
        
        const labelFor = document.querySelector(`label[for="${input.id}"]`)
        if (labelFor) {
            const clone = labelFor.cloneNode(true)
            return clone.textContent.trim()
        }
        
        return null
    }

    updateSelectedText(text = null) {
        if (text) {
            this.setDisplayText(text)
            return
        }

        if (!this.hasOptionsTarget) return

        const selectedCheckboxes = this.optionsTarget.querySelectorAll('input[type="checkbox"]:checked')
        if (selectedCheckboxes.length > 0) {
            if (!this.hasSearchInputTarget) {
                this.selectedTarget.textContent = `${selectedCheckboxes.length} selected`
            }
            return
        }
        
        const selectedRadio = this.optionsTarget.querySelector('input[type="radio"]:checked')
        if (selectedRadio) {
            const optionText = this.getLabelText(selectedRadio) || selectedRadio.value
            this.setDisplayText(optionText)
        } else {
            this.setDisplayText("", "Select or search careers...")
        }
    }

    setDisplayText(text, placeholder = null) {
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.value = text
            if (placeholder) this.searchInputTarget.placeholder = placeholder
        } else if (this.hasSelectedTarget) {
            this.selectedTarget.textContent = text || "Select"
        }
    }

    updateDropdownVisibility() {
        if (this.hasOptionsWrapperTarget) {
            if (this.dropdownOpenValue) {
                this.optionsWrapperTarget.classList.remove("hidden")
                requestAnimationFrame(() => this.calculateMaxHeight())
            } else {
                this.optionsWrapperTarget.classList.add("hidden")
                if (this.hasNoResultsTarget) {
                    this.noResultsTarget.classList.add('hidden')
                }
            }
        }

        if (this.hasDropdownTarget) {
            this.dropdownTarget.setAttribute('aria-expanded', this.dropdownOpenValue ? 'true' : 'false')
        }

        if (this.hasIconTarget) {
            const icon = this.iconTarget.querySelector('svg')
            if (icon) {
                icon.style.transform = this.dropdownOpenValue ? 'rotate(180deg)' : 'rotate(0deg)'
            }
        }
    }

    calculateMaxHeight() {
        if (!this.hasOptionsTarget || !this.hasOptionsWrapperTarget) return

        const triggerElement = this.hasDropdownTarget ? this.dropdownTarget : this.element
        const triggerRect = triggerElement.getBoundingClientRect()
        const spaceBelow = window.innerHeight - triggerRect.bottom
        
        const modal = this.element.closest('[id*="create-user-form"], [id*="modal"], .form-bg, [class*="form-bg"]')
        const footerElement = modal?.querySelector('[data-controller*="cancel-warning"], footer, [class*="footer"]') 
            || document.querySelector('footer')
        
        let maxHeight = spaceBelow - 20
        
        if (footerElement) {
            const spaceBeforeFooter = footerElement.getBoundingClientRect().top - triggerRect.bottom
            if (spaceBeforeFooter > 0 && spaceBeforeFooter < maxHeight) {
                maxHeight = spaceBeforeFooter - 20
            }
        }
        
        maxHeight = Math.max(100, Math.min(maxHeight, 400))
        this.optionsTarget.style.maxHeight = `${maxHeight}px`
    }

    search(event) {
        if (!this.hasOptionsTarget) return

        const searchTerm = event.target.value.toLowerCase()
        
        if (!this.dropdownOpenValue) {
            this.openDropdown()
        }

        if (!searchTerm) {
            this.showAllOptions()
            return
        }

        this.filterOptions(searchTerm)
    }

    showAllOptions() {
        this.optionsTarget.querySelectorAll('.pillar-group-container').forEach(container => {
            container.style.display = ''
        })
        this.optionsTarget.querySelectorAll('label').forEach(option => {
            option.closest('div')?.style.setProperty('display', '')
        })
        if (this.hasNoResultsTarget) {
            this.noResultsTarget.classList.add('hidden')
        }
        this.updateCheckboxStates()
    }

    filterOptions(searchTerm) {
        let hasAnyMatch = false

        this.optionsTarget.querySelectorAll('.pillar-group-container').forEach(container => {
            let hasMatch = false
            container.querySelectorAll('label').forEach(label => {
                const matches = label.textContent.toLowerCase().includes(searchTerm)
                const labelDiv = label.closest('div')
                if (labelDiv) {
                    labelDiv.style.display = matches ? '' : 'none'
                }
                if (matches) {
                    hasMatch = true
                    hasAnyMatch = true
                }
            })
            container.style.display = hasMatch ? '' : 'none'
        })

        this.optionsTarget.querySelectorAll('label').forEach(option => {
            if (option.closest('.pillar-group-container')) return
            
            const matches = option.textContent.toLowerCase().includes(searchTerm)
            const parentDiv = option.closest('div')
            
            if (parentDiv) {
                parentDiv.style.display = matches ? '' : 'none'
            } else {
                option.style.display = matches ? '' : 'none'
            }
            
            if (matches) hasAnyMatch = true
        })

        if (this.hasNoResultsTarget) {
            this.noResultsTarget.classList.toggle('hidden', hasAnyMatch)
        }
        
        // Ensure disabled states are maintained after filtering
        this.updateCheckboxStates()
    }

    updateChips() {
        if (!this.hasChipsContainerTarget || !this.hasOptionsTarget) return

        this.chipsContainerTarget.innerHTML = ''
        const checkedCheckboxes = this.optionsTarget.querySelectorAll('input[type="checkbox"]:checked')
        
        checkedCheckboxes.forEach(checkbox => {
            const label = checkbox.closest('label')
            if (!label) return

            const clone = label.cloneNode(true)
            clone.querySelector('input[type="checkbox"]')?.remove()
            const careerText = clone.textContent.trim()

            const chip = this.createChip(careerText, checkbox.value)
            this.chipsContainerTarget.appendChild(chip)
        })
    }

    createChip(text, value) {
        const chip = document.createElement('div')
        chip.className = 'chip cursor-pointer'
        chip.style.cssText = 'display: flex; min-height: 24px; max-height: 24px; padding: 0 5px 0 12px; align-items: center; gap: 6px; border-radius: 24px; background: var(--Neutral-dark, #58606E);'
        chip.dataset.value = value
        chip.setAttribute('data-action', 'click->select#handleChipClick')
        
        const span = document.createElement('span')
        span.textContent = text
        span.className = 'text-white text-sm font-semibold leading-[140%]'
        
        const cancelIcon = document.createElement('span')
        cancelIcon.className = 'flex-shrink-0'
        cancelIcon.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_5219_3457)"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 1.16667C3.77834 1.16667 1.16667 3.77834 1.16667 7C1.16667 10.2217 3.77834 12.8333 7 12.8333C10.2217 12.8333 12.8333 10.2217 12.8333 7C12.8333 3.77834 10.2217 1.16667 7 1.16667ZM0 7C0 3.13401 3.13401 0 7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M9.06214 4.93785C9.28995 5.16566 9.28995 5.53501 9.06214 5.76281L5.76281 9.06215C5.53501 9.28995 5.16566 9.28995 4.93785 9.06215C4.71005 8.83434 4.71005 8.46499 4.93785 8.23719L8.23719 4.93785C8.46499 4.71005 8.83434 4.71005 9.06214 4.93785Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M4.93785 4.93785C5.16566 4.71005 5.53501 4.71005 5.76281 4.93785L9.06214 8.23719C9.28995 8.46499 9.28995 8.83434 9.06214 9.06215C8.83434 9.28995 8.46499 9.28995 8.23719 9.06215L4.93785 5.76281C4.71005 5.53501 4.71005 5.16566 4.93785 4.93785Z" fill="white"/></g><defs><clipPath id="clip0_5219_3457"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>'
        
        chip.appendChild(span)
        chip.appendChild(cancelIcon)
        return chip
    }

    handleChipClick(event) {
        const checkbox = this.optionsTarget.querySelector(`input[type="checkbox"][value="${event.currentTarget.dataset.value}"]`)
        if (checkbox) {
            checkbox.checked = false
            checkbox.dispatchEvent(new Event('change', { bubbles: true }))
        }
        this.updateChips()
        this.updateSelectedText()
        this.updateCheckboxStates()
    }

    updateCheckboxStates() {
        if (!this.hasOptionsTarget) return

        const checkboxes = this.optionsTarget.querySelectorAll('input[type="checkbox"]')
        const selectedCount = this.optionsTarget.querySelectorAll('input[type="checkbox"]:checked').length
        const maxSelections = 5

        checkboxes.forEach(checkbox => {
            checkbox.disabled = selectedCount >= maxSelections && !checkbox.checked
        })
    }
}

